import React, { useCallback, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from '@mantine/form'
import { Box, Button, Checkbox, Group, Stack, Text, TextInput } from '@mantine/core'
import { IconId, IconPhone, IconUser } from '@tabler/icons-react'
import { Dialog } from 'antd-mobile'
import useOrderCreation from '../hooks/useOrderCreation'
import { usePaymentChannels } from '../../hooks/usePaymentChannels'
import { TemplateMerchantInterface } from '../../typings/orderTypes'
import TermsOfServiceLabel from './TermsOfServiceLabel'
import VerificationCodeInput from './VerificationCodeInput'

type VerificationMode = 'sms' | 'faceid'

export default function QueryReportForm({ template }: { template: TemplateMerchantInterface | undefined }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const linkId = searchParams.get('linkId') || ''
  const [verificationMode, setVerificationMode] = useState<VerificationMode>('sms')
  const { getRoutersString } = usePaymentChannels({ linkId })

  const form = useForm({
    initialValues: {
      name: '',
      phone: '',
      idCard: '',
      smsCode: '',
      termsOfService: false,
    },
    validate: {
      phone: (value) => (value === '' ? '请输入手机号' : !/^1[3-9]\d{9}$/.test(value) ? '请输入正确的手机号' : null),
      smsCode: (value) =>
        verificationMode === 'faceid'
          ? null
          : value === ''
            ? '请输入验证码'
            : value.length !== 6
              ? '请输入6位的验证码'
              : null,
      idCard: (value) =>
        value === ''
          ? '请输入身份证号'
          : !/(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(value)
            ? '请输入正确的身份证号'
            : null,
      name: (value) => (value === '' ? '请输入姓名' : null),
    },
  })

  const { loading, createOrder } = useOrderCreation({
    linkId,
    verificationMode,
    template,
    getRoutersString,
  })

  const handleSubmit = (values: typeof form.values) => {
    if (!values.termsOfService) {
      Dialog.confirm({
        content: <TermsOfServiceLabel />,
        confirmText: '同意并查询',
        cancelText: '不同意',
        onConfirm: () => {
          form.setFieldValue('termsOfService', true)
          createOrder(values)
        },
      }).then()
    } else {
      createOrder(values)
    }
  }

  const handleSwitchToFaceid = useCallback(() => {
    Dialog.confirm({
      title: '切换为人脸识别验证',
      content:
        '您将使用人脸识别进行验证，请确保：\n\n1. 输入的姓名和身份证号准确无误\n2. 光线充足且正对摄像头\n\n提交后将跳转到人脸识别页面',
      confirmText: '确认切换',
      cancelText: '不切换',
      onConfirm: () => {
        setVerificationMode('faceid')
        form.setFieldValue('smsCode', '')
      },
    })
  }, [form])

  return (
    <Box>
      <div className={'flex justify-between'}>
        <div className={'text-left text-lg font-bold'}>{template?.name}</div>
        <div
          className={'text-orange-500 text-sm'}
          onClick={() => {
            navigate('/reportSamples/' + template?.templateId)
          }}
        >
          示例报告
        </div>
      </div>
      <div className={'text-gray-500 text-sm mb-4'}>为保证数据准确，请输入真实信息</div>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput placeholder="请输入本人姓名" leftSection={<IconUser size={16} />} {...form.getInputProps('name')} />
        <TextInput
          mt="md"
          placeholder="请输入本人身份证号"
          leftSection={<IconId size={16} />}
          {...form.getInputProps('idCard')}
        />
        <TextInput
          mt="md"
          placeholder="请输入本人手机号"
          leftSection={<IconPhone size={16} />}
          {...form.getInputProps('phone')}
        />
        {verificationMode === 'sms' && (
          <VerificationCodeInput
            value={form.values.smsCode}
            onChange={(value) => form.setFieldValue('smsCode', value)}
            error={form.errors.smsCode}
            phone={form.values.phone}
            onValidatePhone={() => form.validateField('phone')}
            onSwitchToFaceid={handleSwitchToFaceid}
          />
        )}
        <Checkbox
          mt="md"
          color={'#3A55DD'}
          label={<TermsOfServiceLabel />}
          {...form.getInputProps('termsOfService', { type: 'checkbox' })}
        />

        <Group justify="space-between" mt="md">
          <Button
            loading={loading}
            type="submit"
            fullWidth
            radius="md"
            size={'md'}
            styles={{
              root: {
                backgroundColor: '#3A55DD',
              },
            }}
          >
            <Text>{`¥${(template?.price ?? 0).toFixed(2)} 立即查询`}</Text>
          </Button>
        </Group>

        <Group className="bg-[#FFF2F2] rounded-lg p-3 mt-4" align="flex-start">
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="text-sm text-[#FF4D4F] leading-5 block">
              我司不提供央行征信和个人爬虫等隐私数据查询，也不提供贷款及信用修复等业务。所有要求你进行汇款、转账、非法刷单、买理财、承诺包下款的操作，都是诈骗！
            </span>
          </div>
        </Group>

        <Stack className={'mt-4 text-sm text-gray-500'} gap={4}>
          <span>1. 已付费查询用户可点击右侧历史报告查看报告。 </span>
          <span>2. 付费之后报告在十分钟内未生成或者对报告数据存在任何疑问，可点击右下角按钮联系客服咨询。 </span>
        </Stack>
      </form>
    </Box>
  )
}
